/**
 * SuggestionsTab — iter86: Admin review queue for user-submitted source
 * suggestions (from the feed "Suggest a source" form). Self-contained:
 * loads on mount (Radix mounts TabsContent only when active).
 * Actions: Mark added / Dismiss / Reopen. No email, no MC events.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../config";
import { TabsContent } from "../ui/tabs";
import { RefreshCw, ExternalLink, Check, X, RotateCcw, Inbox } from "lucide-react";

const STATUS_STYLE = {
  new:       { label: "NEW",       light: "bg-[#006874]/10 text-[#006874]", dark: "bg-cyan-400/15 text-cyan-300" },
  added:     { label: "ADDED",     light: "bg-emerald-100 text-emerald-700", dark: "bg-emerald-400/15 text-emerald-300" },
  dismissed: { label: "DISMISSED", light: "bg-[#f3f4f5] text-[#9aa7a9]",    dark: "bg-[#252e2f] text-slate-500" },
};

export default function SuggestionsTab({ dm, authHeader, onAddAsSource, onCountsChange }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/source-suggestions`, { headers: authHeader() });
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
      onCountsChange?.(r.data.counts || {});
    } catch {
      toast.error("Could not load suggestions");
    } finally {
      setLoading(false);
    }
  }, [authHeader]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await axios.patch(`${API}/admin/source-suggestions/${id}`, { status }, { headers: authHeader() });
      setItems(prev => {
        const next = prev.map(s => (s.id === id ? { ...s, status } : s));
        const c = { new: 0, added: 0, dismissed: 0 };
        next.forEach(s => { if (c[s.status] != null) c[s.status] += 1; });
        setCounts(c);
        onCountsChange?.(c);
        return next;
      });
    } catch {
      toast.error("Update failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <TabsContent value="suggestions">
      <div className="max-w-3xl">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <h2 className={`editorial-heading text-2xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Source Suggestions</h2>
            <p className={`category-tag mt-1 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
              Submitted by users from the feed. {counts.new || 0} new · {counts.added || 0} added · {counts.dismissed || 0} dismissed
            </p>
          </div>
          <button onClick={load} data-testid="suggestions-refresh" className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full category-tag ${dm ? "bg-[#1a2122] text-slate-300" : "bg-white text-[#3b494c]"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.12)" }}>
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><RefreshCw className={`w-5 h-5 animate-spin ${dm ? "text-cyan-400" : "text-[#006874]"}`} /></div>
        ) : items.length === 0 ? (
          <div className={`rounded-2xl p-12 text-center ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1)" }} data-testid="suggestions-empty">
            <Inbox className={`w-8 h-8 mx-auto mb-3 opacity-30 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`} />
            <p className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>No suggestions yet — they&apos;ll appear here as users submit them from the feed.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(s => {
              const st = STATUS_STYLE[s.status] || STATUS_STYLE.new;
              return (
                <div key={s.id} data-testid={`suggestion-row-${s.id}`}
                  className={`rounded-2xl p-4 ${dm ? "bg-[#1a2122]" : "bg-white"}`}
                  style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1)" }}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`text-[9px] font-bold tracking-wider px-2 py-0.5 rounded-full ${dm ? st.dark : st.light}`}>{st.label}</span>
                    <a href={s.url} target="_blank" rel="noopener noreferrer"
                      className={`text-sm font-medium hover:underline inline-flex items-center gap-1 truncate max-w-[55%] ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
                      {s.url.replace(/^https?:\/\//, "")} <ExternalLink className="w-3 h-3 shrink-0 opacity-50" />
                    </a>
                    <span className={`category-tag ml-auto ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
                      {s.user_email || "unknown"} · {(s.created_at || "").slice(0, 10)}
                    </span>
                  </div>
                  {s.note && <p className={`text-xs mt-2 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{s.note}</p>}
                  <div className="flex items-center gap-2 mt-3">
                    {s.status !== "added" && (
                      <button
                        onClick={async () => {
                          await setStatus(s.id, "added");
                          onAddAsSource && onAddAsSource(s.url);
                        }}
                        disabled={busyId === s.id} data-testid={`suggestion-add-${s.id}`}
                        className={`flex items-center gap-1 px-3 py-1 rounded-full category-tag disabled:opacity-40 ${dm ? "bg-emerald-400/15 text-emerald-300" : "bg-emerald-50 text-emerald-700"}`}>
                        <Check className="w-3 h-3" /> Add as source
                      </button>
                    )}
                    {s.status !== "dismissed" && (
                      <button onClick={() => setStatus(s.id, "dismissed")} disabled={busyId === s.id} data-testid={`suggestion-dismiss-${s.id}`}
                        className={`flex items-center gap-1 px-3 py-1 rounded-full category-tag disabled:opacity-40 ${dm ? "bg-[#252e2f] text-slate-400" : "bg-[#f3f4f5] text-[#6b7a7d]"}`}>
                        <X className="w-3 h-3" /> Dismiss
                      </button>
                    )}
                    {s.status !== "new" && (
                      <button onClick={() => setStatus(s.id, "new")} disabled={busyId === s.id} data-testid={`suggestion-reopen-${s.id}`}
                        className={`flex items-center gap-1 px-3 py-1 rounded-full category-tag disabled:opacity-40 ${dm ? "bg-cyan-400/10 text-cyan-300" : "bg-[#006874]/8 text-[#006874]"}`}>
                        <RotateCcw className="w-3 h-3" /> Reopen
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </TabsContent>
  );
}
